import {Breadcrumb} from '@/components/atoms'
import {PageHeader, TabsComponent} from '@/components/molecules'
import {BaseDataEdit, BrandAndCategoryEdit, MediaGalleryEdit, ProductDisplayEdit} from '@/containers'
import {TaxAndSalesEdit} from '@/containers/ProductReviewEditContainer/TaxAndSales/TaxAndSalesEdit'
import {updateProduct, uploadProductMedia} from '@/services/ProductsService/product'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useQueryClient} from '@tanstack/react-query'
import {useEffect, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useProductOverview} from './ProductReview/useProductOverview'

export const ProductReviewEdit = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const {productOverviewData} = useProductOverview()
  const [formData, setFormData] = useState({})

  const location = useLocation()
  const state = location?.state

  useEffect(() => {
    if (productOverviewData?.data) {
      setFormData(productOverviewData.data)
    }
  }, [productOverviewData])

  const handleFormChange = updatedData => {
    setFormData(prevData => {
      const newData = {...prevData, ...updatedData}
      return newData
    })
  }

  const handleSaveProduct = async (dataToSave, isMediaUpdate = false) => {
    try {
      let response

      if (isMediaUpdate && dataToSave?.image) {
        // Handle media upload with file
        const formDataToSend = new FormData()
        formDataToSend.append('image', dataToSave.image, dataToSave.image.name)
        response = await uploadProductMedia(id, formDataToSend)

        setFormData(prev => ({
          ...prev,
          media: {
            mediaUrl: response?.mediaUrl || prev.media?.mediaUrl,
            title: dataToSave.media.title || prev.media?.title,
          },
        }))
      } else {
        // Regular product update
        const dataToSubmit = dataToSave || formData

        // Convert empty strings to null in dataToSubmit
        const convertEmptyStringsToNull = obj => {
          if (!obj || typeof obj !== 'object') return obj

          return Object.entries(obj).reduce(
            (acc, [key, value]) => {
              if (typeof value === 'object') {
                acc[key] = convertEmptyStringsToNull(value)
              } else {
                acc[key] = value === '' ? null : value
              }
              return acc
            },
            Array.isArray(obj) ? [] : {}
          )
        }

        const sanitizedData = convertEmptyStringsToNull(dataToSubmit)

        response = await updateProduct(sanitizedData)
      }

      const targetId = id || response?.id || response?.productId
      if (targetId) {
        // Invalidate both the product list and the individual product queries
        await queryClient.invalidateQueries(['PRODUCT_LIST'])
        await queryClient.invalidateQueries(['PRODUCT', targetId])

        navigate(ROUTES.PRODUCTS_LIST)
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Product Successfully Updated',
            type: 'success',
          })
        )
      } else {
        console.warn('Could not determine ID for redirection after save.')
      }

      return response
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: `Error saving product: ${error}`,
          type: 'error',
        })
      )
      throw error
    }
  }

  if (!formData || Object.keys(formData).length === 0) {
    return <div>Loading ...</div>
  }

  const productName = formData.name || formData.productName || formData.baseData?.name || 'Loading...'
  const tabList = [
    {
      id: 'Base Data',
      tabLabel: 'Base Data',
      tabComponent: <BaseDataEdit formData={formData} onDataChange={handleFormChange} onPublish={handleSaveProduct} />,
    },
    {
      id: 'Media',
      tabLabel: 'Media',
      tabComponent: (
        <MediaGalleryEdit
          formData={formData}
          onDataChange={handleFormChange}
          handleSave={handleSaveProduct}
          productId={id}
        />
      ),
    },
    {
      id: 'Display',
      tabLabel: 'Display',
      tabComponent: (
        <ProductDisplayEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Brand & Category',
      tabLabel: 'Brand & Category',
      tabComponent: (
        <BrandAndCategoryEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Tax & Sales ',
      tabLabel: 'Tax & Sales ',
      tabComponent: (
        <TaxAndSalesEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
  ]

  const breadcrumbs = [
    {href: '/', label: ''},
    {href: ROUTES.PRODUCTS_LIST, label: 'Product view'},
    {href: '', label: productName},
  ]

  return (
    <>
      <Breadcrumb uiSchema={{isDark: true, className: 'mb-5', breadcrumbs}} id="productOverviewBreadcrumb" />
      <div className="mb-4 flex items-center justify-between">
        <PageHeader title={productName} />
      </div>
      <TabsComponent
        isDarkTab={false}
        isBorderVisible={false}
        tabList={tabList}
        defaultTabIndex={state?.selectedTabNumb ?? 0}
        removeBG={true}
      />
    </>
  )
}

export default ProductReviewEdit
